import React from 'react';
import classnames from 'classnames';
import {
  DisplayField,
} from 'hellospa/components/signature-request';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import { isHidden } from 'common/components/hello-modal';
import { signerContext } from './context';
import { signatureRequestContext, zoomContext } from '../signature-request/context';
import TextField from './text-field';
import styles from './signer-field.scss';
import SignaturePlaceholder from './signature-placeholder';

export const debugRenderContext = React.createContext((_id) => {});

function SignerField(props) {
  const {
    fieldData, textScale, isActive, updateFields, selectField,
    validationErrors, getInstantValidationErrorHack,
  } = props;
  const selfRef = React.useRef();
  const fieldRef = React.useRef();
  const { zoomIntoField } = React.useContext(zoomContext);
  const debugRender = React.useContext(debugRenderContext);
  debugRender(fieldData.id);

  const handleFocus = React.useCallback(() => {
    if (!isActive) {
      selectField(fieldData.id);
    }
  }, [fieldData.id, isActive, selectField]);

  const handleTextFieldTouchEnd = React.useCallback(() => {
    zoomIntoField(fieldData);
  }, [fieldData, zoomIntoField]);

  React.useEffect(() => {
    if (fieldData.type === 'dropdown') {
      if (isActive) {
        // This has to be delayed because without it clicking the dropdown will:
        // 1. focus this field
        // 2. (without delay) this code will open the modal
        // 3. the click handler is triggered and closes the modal
        setTimeout(() => {
          Dropdown.openMenu(fieldData.id, { focusMenu: true });
        }, 100);
      } else {
        Dropdown.closeMenu(fieldData.id, { focusButton: false });
      }
    }
  }, [fieldData.id, fieldData.type, isActive]);

  React.useEffect(() => {

    // Do not run checks for IE 11
    // This is trial-and-error because IE11 crashes
    // in browserstack when you open the console
    if (navigator.userAgent.match(/Trident\/7\./)) {
      return;
    }

    if (
      isActive
      && selfRef.current != null
      && fieldRef.current != null
      && !isHidden(selfRef.current)
    ) {
      // This needs to be delayed slightly so that when you tab from one field
      // to the next, `handleFocus` can change the selected field before this
      // triggers and pulls the focus back. DEV-8823
      const t = setTimeout(() => {
        if (!selfRef.current.contains(document.activeElement)) {
          fieldRef.current.focus();

          if (
            !selfRef.current.contains(document.activeElement)
            && process.env.NODE_ENV === 'develop'
          ) {
            // eslint-disable-next-line no-console
            console.warn('Focus failed', fieldData.id);
          }
        }
      }, 1);

      // It's imporant to clear the timeout if the component re-renders. Without
      // this, when you tab from one field to the next, the two end up fighting
      // for focus in an endless loop.
      return () => {
        clearTimeout(t);
      };
    }
  }); // Run on every render so that clicking next re-focusses the correct field
  // even if it was already marked "active"

  const updateField = React.useCallback(
    (update) => {
      if (fieldData.type === 'radiobutton') {
        if (update.checked) {
          updateFields({
            [fieldData.id]: { checked: true },
          });
        } else if (update.checked === false) {
          // radio buttons can't be de-selected. You can only move the selection
          // to another button in the group.
          return;
        }

      }

      updateFields({ [fieldData.id]: update });
    },
    [fieldData.id, fieldData.type, updateFields],
  );

  const onChange = React.useCallback((e) => {
    const update = (e.target.checked != null)
      ? { checked: e.target.checked }
      : { value: e.target.value };
    updateField(update);
  }, [updateField]);


  const checkboxKeypressHandler = React.useCallback(
    (e) => {
      // IE: Ctrl +/- are "Unidentified", so it needs to fall back on the keyCode
      let key = e.key !== 'Unidentified' ? e.key : undefined;
      // Safari doesn't support .key, so we have to fall back to .keyCode
      key = key || e.keyCode;
      switch (key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          updateField({ checked: !fieldData.checked });
          break;
        default:
      }
    },
    [fieldData.checked, updateField],
  );

  const checkboxTouchEndHandler = React.useCallback(
    () => {
      // iOS doesn't respect <label htmlFor="" targeting checkboxes, we we have
      // to use this to simulate that behavior. Android devices can't run this
      // code because the two operations cancel each other out.
      if (navigator.userAgent.match(/iphone|ipad/gi) !== null) {
        // Setting field to active so it can trigger validation errors
        if (!isActive) {
          selectField(fieldData.id);
        }
        updateField({ checked: !fieldData.checked });
      }
    },
    [isActive, fieldData.checked, fieldData.id, updateField, selectField],
  );

  let field;


  switch (fieldData.type) {
    case 'signature':
    case 'initials':
      field = (
        <SignaturePlaceholder
          fieldData={fieldData}
          textScale={textScale}
          onTouchEnd={handleFocus}
          ref={fieldRef}
          {...props} />
      );
      break;
    case 'text':
      field = (
        <TextField
          {...props}
          ref={fieldRef}
          getInstantValidationErrorHack={getInstantValidationErrorHack}
          isOverlay={props.isOverlay}
          documentPreview={props.documentPreview}
          updateField={updateField}
          onTouchEnd={handleTextFieldTouchEnd}
          onChange={onChange} />
      );
      break;
    case 'dropdown':
      field = (
        <DisplayField
          {...props}
          hideControls={Boolean(fieldData.readOnly)}
          tabIndex={0}
          ref={fieldRef}
          onChange={onChange} />
      );
      break;
    case 'checkbox':
    case 'radiobutton':
      field = (
        <DisplayField
          {...props}
          disabled={fieldData.type === 'date'}
          tabIndex={fieldData.type === 'date' ? -1 : 0}
          onKeyPress={checkboxKeypressHandler}
          onTouchEnd={checkboxTouchEndHandler}
          ref={fieldRef}
          onChange={onChange} />
      );
      break;
    case 'hyperlink':
      field = (
        <DisplayField
          {...props}
          ref={fieldRef} />
      );
      break;
    default: {
      field = (
        <DisplayField
          {...props}
          disabled={fieldData.type === 'date'}
          tabIndex={fieldData.type === 'date' ? -1 : 0}
          ref={fieldRef}
          onChange={onChange} />
      );
    }
  }

  return (
    <div onFocus={handleFocus} ref={selfRef} className={styles.signerField}>
      {fieldData.type === 'checkbox' && (
        <div className={styles.checkbox} />
      )}
      {field}
      <div
        style={{
          // The old signer app makes the required * 20px tall, but this needs
          // to account for textScale/zoom
          fontSize: `${20 * textScale}px`,
        }}
        className={classnames(
          styles[fieldData.type],
          {
            // Only adding the field border for non readOnly fields
            [styles.fieldBorder]: !fieldData.readOnly && fieldData.type !== 'hyperlink',
            // The required asterisk is supposed to dissapear when you start
            // typing, but it returns when you move on to the next field.
            [styles.required]: fieldData.required && (
              // non-active required fields always show the *
              !isActive
              // Active required fields hide the * when you start typing
              || fieldData.value == null
            ),
            [styles.active]: isActive,
            [styles.error]: validationErrors[fieldData.id] != null,
          },
        )} />
    </div>
  );
}

const MemoSignerField = React.memo(SignerField);
export default function SignerFieldContext(props) {
  const {
    selectedFieldIds, isOverlay, documentPreview,
  } = React.useContext(signatureRequestContext);
  const {
    updateFields, selectField, validationErrors, getInstantValidationErrorHack,
  } = React.useContext(signerContext);

  return (
    <MemoSignerField
      {...props}
      updateFields={updateFields}
      selectField={selectField}
      validationErrors={validationErrors}
      getInstantValidationErrorHack={getInstantValidationErrorHack}
      isOverlay={isOverlay}
      documentPreview={documentPreview}
      isActive={selectedFieldIds.includes(props.fieldData.id)} />
  );
}
